/**
 * Financial Diagnostic Action Card Component
 * Displays action button to start the financial diagnostic wizard
 */

import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';
import { ActionButton } from '../../api/types';

interface FinancialDiagnosticActionCardProps {
  actionButton: ActionButton;
  onStartWizard: () => void;
  isLoading?: boolean;
}

export default function FinancialDiagnosticActionCard({
  actionButton,
  onStartWizard,
  isLoading = false,
}: FinancialDiagnosticActionCardProps) {
  const buttonStyle = actionButton.style || 'primary';

  const getButtonColor = () => {
    switch (buttonStyle) {
      case 'success':
        return colors.success;
      case 'secondary':
        return colors.backgroundSecondary;
      case 'primary':
      default:
        return colors.primary;
    }
  };

  const getButtonTextColor = () => {
    switch (buttonStyle) {
      case 'secondary':
        return colors.text;
      case 'success':
      case 'primary':
      default:
        return '#FFFFFF';
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.iconContainer}>
        <Text style={styles.icon}>📊</Text>
      </View>

      <Text style={styles.title}>État des lieux financier</Text>
      <Text style={styles.description}>
        Complète ton instantané financier en moins de 2 minutes pour recevoir un diagnostic
        personnalisé et un plan d'action concret.
      </Text>

      <View style={styles.features}>
        <Text style={styles.feature}>✓ 5 questions simples</Text>
        <Text style={styles.feature}>✓ Analyse IA personnalisée</Text>
        <Text style={styles.feature}>✓ Objectif automatique proposé</Text>
      </View>

      <TouchableOpacity
        style={[
          styles.button,
          { backgroundColor: getButtonColor() },
          isLoading && styles.buttonDisabled,
        ]}
        onPress={onStartWizard}
        disabled={isLoading}
        activeOpacity={0.5}
      >
        <Text
          style={[
            styles.buttonText,
            { color: getButtonTextColor() },
          ]}
        >
          {isLoading ? 'Démarrage...' : actionButton.label}
        </Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 12,
    padding: spacing.lg,
    marginVertical: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
  },
  iconContainer: {
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  icon: {
    fontSize: 40,
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    marginBottom: spacing.xs,
    textAlign: 'center',
  },
  description: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginBottom: spacing.md,
    textAlign: 'center',
    lineHeight: typography.lineHeight.relaxed * typography.fontSize.sm,
  },
  features: {
    backgroundColor: colors.background,
    borderRadius: 8,
    padding: spacing.md,
    marginBottom: spacing.md,
  },
  feature: {
    fontSize: typography.fontSize.sm,
    color: colors.text,
    marginBottom: spacing.xs,
    lineHeight: typography.lineHeight.normal * typography.fontSize.sm,
  },
  button: {
    borderRadius: 8,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    alignItems: 'center',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  buttonText: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
  },
});
